import React, { useCallback } from 'react';

import { defineMessage, useIntl, FormattedMessage } from 'react-intl';

import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { LinkIconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import SettingsIcon from '@atlaskit/icon/core/settings';
import Lozenge from '@atlaskit/lozenge';
import { Box, Flex } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';

import { getAgentIdFromAri } from '../utils';

const settingsButtonLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.CreateAgentCardPublished.settingsButtonLabel',
	defaultMessage: 'Open agent settings',
	description: 'Accessible label for the settings cog icon button in the completed card footer.',
});

const publishedStateLabel = defineMessage({
	id: 'conversation-assistant-message-actions.controllers.studio-create-agent.create-agent-card.CreateAgentCardPublished.publishedStateLabel',
	defaultMessage: 'Published',
	description:
		'Default label shown in the completed-state lozenge to indicate the agent has been published and is live.',
});

const styles = cssMap({
	card: {
		width: '100%',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('elevation.surface'),
	},
	titleRow: {
		flex: 1,
		minWidth: '0',
	},
	avatarWrapper: {
		flexShrink: 0,
	},
	titleText: {
		flex: '1 1 auto',
		minWidth: '0',
		overflow: 'hidden',
	},
	titleSpan: {
		display: 'block',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

type CreateAgentCardPublishedProps = {
	agentAri: string;
	agentName: string;
	imageUrl?: string;
	agentIdentityAccountId?: string;
	agentNamedId?: string;
	agentEditUrl?: string;
};

export const CreateAgentCardPublished = ({
	agentAri,
	agentName,
	imageUrl,
	agentIdentityAccountId,
	agentNamedId,
	agentEditUrl,
}: CreateAgentCardPublishedProps): React.JSX.Element => {
	const intl = useIntl();
	const { sendAnalyticsEvent } = useAnalytics();

	const handleSettingsClick = useCallback(
		(_: React.MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			sendAnalyticsEvent(
				analyticsEvent.update({
					actionSubjectId: 'editAgentInStudio',
					attributes: {
						analyticsSource: 'inline-create-agent-card',
						agentId: getAgentIdFromAri(agentAri) ?? undefined,
					},
				}),
			);
		},
		[sendAnalyticsEvent, agentAri],
	);
	return (
		<Box xcss={styles.card} testId="create-agent-card-published">
			<Flex justifyContent="space-between" alignItems="center" gap="space.100">
				<Flex alignItems="center" gap="space.100" xcss={styles.titleRow}>
					<Box xcss={styles.avatarWrapper}>
						<AgentAvatar
							agentId={agentAri}
							agentIdentityAccountId={agentIdentityAccountId}
							agentNamedId={agentNamedId}
							imageUrl={imageUrl}
							name={agentName}
							size="medium"
						/>
					</Box>
					<Box xcss={styles.titleText}>
						<Heading size="xsmall" as="h3">
							<Box as="span" title={agentName} xcss={styles.titleSpan}>
								{agentName}
							</Box>
						</Heading>
					</Box>
				</Flex>
				<Flex alignItems="center" gap="space.100">
					<Lozenge appearance="success">
						<FormattedMessage {...publishedStateLabel} />
					</Lozenge>
					{agentEditUrl && (
						<LinkIconButton
							icon={SettingsIcon}
							label={intl.formatMessage(settingsButtonLabel)}
							href={agentEditUrl}
							target="_blank"
							onClick={handleSettingsClick}
							testId="create-agent-card-published-settings-link"
							isTooltipDisabled={false}
						/>
					)}
				</Flex>
			</Flex>
		</Box>
	);
};
